import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Row, Col, Typography, Tag, Statistic, Table, message, Spin,
  Button, Tabs, DatePicker, Select, Space,
} from 'antd';
import {
  DollarOutlined, RiseOutlined, FallOutlined, TeamOutlined,
  ReloadOutlined, UserAddOutlined, ThunderboltOutlined, WarningOutlined,
  ClockCircleOutlined, LineChartOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const BUCKET_LABELS = {
  pagante_engajado:     { label: 'Pagante engajado',         color: 'green'  },
  pagante_iniciando:    { label: 'Pagante iniciando',        color: 'cyan'   },
  pagante_sem_uso:      { label: 'Pagante sem uso',          color: 'gold'   },
  pagou1_e_abandonou:   { label: 'Pagou 1x e abandonou',     color: 'orange' },
  cancelou:             { label: 'Cancelou',                 color: 'red'    },
  usou_e_sumiu:         { label: 'Usou e sumiu (trial)',     color: 'purple' },
  trial_meio_termo:     { label: 'Trial meio-termo',         color: 'default'},
  trial_testou_pouco:   { label: 'Trial testou pouco',       color: 'default'},
  trial_nem_abriu:      { label: 'Trial nem abriu',          color: 'default'},
  outros:               { label: 'Outros',                   color: 'default'},
};

const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (d) => d ? dayjs(d).format('DD/MM/YYYY') : '-';

function ReceitaMensalTab() {
  const [ym, setYm] = useState(dayjs().format('YYYY-MM'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/monitoring/receita-mensal', { params: { ym } });
      setData(r.data);
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  }, [ym]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>;
  if (!data) return null;

  const deltaPositivo = data.delta >= 0;
  const b = data.buckets;

  const renderItems = (items, extraCols = []) => (
    <Table
      size="small"
      dataSource={items}
      rowKey="customer_id"
      pagination={{ pageSize: 10, showSizeChanger: false }}
      columns={[
        { title: 'ID', dataIndex: 'customer_id', width: 70 },
        { title: 'Time', dataIndex: 'game_name', ellipsis: true,
          render: (v, r) => <a href={`/customers/${r.customer_id}/ficha`}>{v}</a> },
        { title: 'Plano', dataIndex: 'plan', width: 130,
          render: v => v ? <Tag>{v}</Tag> : '-' },
        { title: 'Status', dataIndex: 'status', width: 120,
          render: v => v ? <Tag color={v === 'ATIVO' ? 'green' : v === 'PLANO_EXPIRADO' ? 'orange' : 'red'}>{v}</Tag> : '-' },
        { title: 'Valor', dataIndex: 'valor', width: 100, align: 'right',
          render: v => <strong>{fmtBRL(v)}</strong> },
        ...extraCols,
      ]}
    />
  );

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Text>Mês:</Text>
        <DatePicker.MonthPicker
          value={dayjs(ym, 'YYYY-MM')}
          onChange={(d) => d && setYm(d.format('YYYY-MM'))}
          allowClear={false}
          format="MM/YYYY"
        />
        <Text type="secondary">comparado com {dayjs(data.mes_anterior, 'YYYY-MM').format('MM/YYYY')}</Text>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
      </Space>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title={`Receita ${dayjs(ym, 'YYYY-MM').format('MM/YYYY')}`}
              value={data.receita_total_atual}
              prefix="R$"
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
            <Text type="secondary">
              {b.novos.count + b.renovacoes.count + b.reativados.count} pagamentos confirmados
            </Text>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title={`Estimada ${dayjs(data.mes_anterior, 'YYYY-MM').format('MM/YYYY')}`}
              value={data.receita_estimada_anterior}
              prefix="R$"
              precision={2}
            />
            <Text type="secondary">base histórica pra comparação</Text>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Delta vs mês anterior"
              value={data.delta}
              prefix={deltaPositivo ? <RiseOutlined /> : <FallOutlined />}
              precision={2}
              valueStyle={{ color: deltaPositivo ? '#52c41a' : '#ff4d4f' }}
              suffix={<span style={{ fontSize: 14 }}>R$</span>}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={5}>
          <Card size="small">
            <Statistic title="Novos" value={b.novos.count} prefix={<UserAddOutlined />}
              valueStyle={{ color: '#52c41a' }} />
            <Text type="secondary">{fmtBRL(b.novos.receita)}</Text>
          </Card>
        </Col>
        <Col xs={12} md={5}>
          <Card size="small">
            <Statistic title="Renovações" value={b.renovacoes.count} prefix={<TeamOutlined />} />
            <Text type="secondary">{fmtBRL(b.renovacoes.receita)}</Text>
          </Card>
        </Col>
        <Col xs={12} md={5}>
          <Card size="small">
            <Statistic title="Reativados" value={b.reativados.count} prefix={<ThunderboltOutlined />}
              valueStyle={{ color: '#1890ff' }} />
            <Text type="secondary">{fmtBRL(b.reativados.receita)}</Text>
          </Card>
        </Col>
        <Col xs={12} md={5}>
          <Card size="small">
            <Statistic title="Churn confirmado" value={b.churn.count} prefix={<WarningOutlined />}
              valueStyle={{ color: '#ff4d4f' }} />
            <Text type="secondary">{fmtBRL(b.churn.receita_perdida)} perdidos</Text>
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card size="small">
            <Statistic title="Ainda no ciclo" value={b.no_ciclo.count} prefix={<ClockCircleOutlined />} />
            <Text type="secondary">{fmtBRL(b.no_ciclo.receita_potencial)} em risco</Text>
          </Card>
        </Col>
      </Row>

      <Tabs items={[
        { key: 'novos', label: `Novos (${b.novos.count})`,
          children: renderItems(b.novos.items) },
        { key: 'renovacoes', label: `Renovações (${b.renovacoes.count})`,
          children: renderItems(b.renovacoes.items) },
        { key: 'reativados', label: `Reativados (${b.reativados.count})`,
          children: renderItems(b.reativados.items,
            [{ title: '1º pgto', dataIndex: 'first_ym', width: 100 }]) },
        { key: 'churn', label: `Churn (${b.churn.count})`,
          children: renderItems(b.churn.items,
            [{ title: 'Venceu em', dataIndex: 'expires_at', width: 120, render: fmtDate }]) },
        { key: 'no_ciclo', label: `No ciclo (${b.no_ciclo.count})`,
          children: renderItems(b.no_ciclo.items,
            [{ title: 'Vence em', dataIndex: 'expires_at', width: 120, render: fmtDate }]) },
      ]} />
    </div>
  );
}

function EntrantesCoorteTab() {
  const [meses, setMeses] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bucketSel, setBucketSel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/monitoring/entrantes-coorte', { params: { meses } });
      setData(r.data);
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  }, [meses]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>;
  if (!data) return null;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Text>Janela:</Text>
        <Select value={meses} onChange={setMeses} style={{ width: 120 }}
          options={[
            { value: 3, label: '3 meses' }, { value: 6, label: '6 meses' },
            { value: 12, label: '12 meses' }, { value: 24, label: '24 meses' },
          ]} />
        <Text type="secondary">{data.total_entrantes} entrantes no periodo</Text>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
      </Space>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        {data.buckets.map(bk => {
          const meta = BUCKET_LABELS[bk.bucket] || { label: bk.bucket, color: 'default' };
          return (
            <Col xs={12} md={6} lg={4} key={bk.bucket} style={{ marginBottom: 12 }}>
              <Card size="small" hoverable
                onClick={() => setBucketSel(bucketSel === bk.bucket ? null : bk.bucket)}
                style={{ borderColor: bucketSel === bk.bucket ? '#1890ff' : undefined,
                         borderWidth: bucketSel === bk.bucket ? 2 : 1 }}>
                <Tag color={meta.color} style={{ marginBottom: 8 }}>{meta.label}</Tag>
                <Statistic value={bk.count} suffix={<Text type="secondary" style={{ fontSize: 12 }}>({bk.pct}%)</Text>} />
              </Card>
            </Col>
          );
        })}
      </Row>

      <Card size="small" title="Distribuição por grupo de entrada" style={{ marginBottom: 16 }}>
        <Table
          size="small"
          dataSource={data.coortes}
          rowKey="coorte"
          pagination={false}
          columns={[
            { title: 'Mês de entrada', dataIndex: 'coorte', width: 130,
              render: v => <Tag>{dayjs(v, 'YYYY-MM').format('MM/YYYY')}</Tag> },
            { title: 'Total', dataIndex: 'total', width: 80 },
            ...['pagante_engajado', 'usou_e_sumiu', 'trial_meio_termo', 'trial_testou_pouco',
                'pagou1_e_abandonou', 'cancelou', 'trial_nem_abriu'].map(k => ({
              title: BUCKET_LABELS[k]?.label || k,
              dataIndex: k, width: 100, align: 'center',
              render: v => v ? <span>{v}</span> : <Text type="secondary">-</Text>,
            })),
          ]}
        />
      </Card>

      <Card size="small" title={
        bucketSel
          ? `Times no bucket: ${BUCKET_LABELS[bucketSel]?.label || bucketSel} (${(data.items[bucketSel] || []).length})`
          : 'Clique num bucket acima pra ver os times'
      }>
        {bucketSel && (
          <Table
            size="small"
            dataSource={data.items[bucketSel] || []}
            rowKey="customer_id"
            pagination={{ pageSize: 15, showSizeChanger: false }}
            columns={[
              { title: 'ID', dataIndex: 'customer_id', width: 70 },
              { title: 'Time', dataIndex: 'game_name', ellipsis: true,
                render: (v, r) => <a href={`/customers/${r.customer_id}/ficha`}>{v}</a> },
              { title: 'Mês de entrada', dataIndex: 'coorte', width: 130,
                render: v => <Tag>{dayjs(v, 'YYYY-MM').format('MM/YYYY')}</Tag> },
              { title: 'Status', dataIndex: 'status', width: 130,
                render: v => v ? <Tag color={v === 'ATIVO' ? 'green' : v === 'PLANO_EXPIRADO' ? 'orange' : 'red'}>{v}</Tag> : '-' },
              { title: 'Plano', dataIndex: 'plan', width: 130 },
              { title: 'Pgtos', dataIndex: 'paid_count', width: 70, align: 'center' },
              { title: 'Convoc', dataIndex: 'games_total', width: 80, align: 'center' },
              { title: 'Últ 60d', dataIndex: 'games_last_60d', width: 80, align: 'center' },
              { title: 'Jogadores', dataIndex: 'players_count', width: 90, align: 'center' },
            ]}
          />
        )}
      </Card>
    </div>
  );
}

export default function ReceitaAnalytics() {
  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          <LineChartOutlined /> Analytics de Receita & Grupos de Entrada
        </Title>
        <Text type="secondary">
          Comparativo mensal (novos vs churn vs renovação) e evolução dos entrantes por mês de entrada.
        </Text>
      </div>

      <Tabs items={[
        { key: 'receita', label: 'Receita mensal', children: <ReceitaMensalTab /> },
        { key: 'coortes', label: 'Grupos de entrada', children: <EntrantesCoorteTab /> },
      ]} />
    </div>
  );
}
